import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LockKey, CheckCircle, XCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("validating"); // validating | valid | invalid | done
  const [target, setTarget] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let done = false;
    axios.get(`${API}/public/reset-password/${token}/validate`)
      .then((res) => { if (!done) { setTarget(res.data); setStatus("valid"); } })
      .catch((e) => { if (!done) { setErr(e.response?.data?.detail || "Invalid or expired reset link."); setStatus("invalid"); } });
    return () => { done = true; };
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters.");
    if (pw !== confirm) return toast.error("Passwords don't match.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/reset-password/${token}`, { password: pw });
      setStatus("done");
      toast.success("Password updated. You can now sign in.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed.");
    } finally { setSubmitting(false); }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md border border-border bg-card rounded-lg p-8 space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-5">
          <div className="w-10 h-10 border border-foreground bg-foreground text-background flex items-center justify-center font-heading font-black">C</div>
          <div>
            <div className="font-heading font-black text-lg leading-none tracking-tight">CASEDIST INC.</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Password Reset</div>
          </div>
        </div>

        {status === "validating" && (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="reset-validating">Verifying reset link…</div>
        )}

        {status === "invalid" && (
          <div className="space-y-3 py-4 text-center" data-testid="reset-invalid">
            <XCircle size={40} weight="fill" className="mx-auto text-rose-600" />
            <div className="font-heading font-bold">Link no longer valid</div>
            <div className="text-sm text-muted-foreground">{err}</div>
            <Link to="/login"><Button variant="outline" className="border-border mt-3">Back to Login</Button></Link>
          </div>
        )}

        {status === "valid" && (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div className="text-sm">
              Resetting password for <span className="font-semibold">{target?.name}</span>
              <div className="text-xs text-muted-foreground font-mono mt-0.5">{target?.email}</div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">New password</Label>
              <Input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="border-border" data-testid="reset-pw-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Confirm password</Label>
              <Input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="border-border" data-testid="reset-confirm-input" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full" data-testid="reset-submit-btn">
              <LockKey size={16} className="mr-2" /> {submitting ? "Updating…" : "Set new password"}
            </Button>
          </form>
        )}

        {status === "done" && (
          <div className="space-y-4 py-4 text-center" data-testid="reset-done">
            <CheckCircle size={40} weight="fill" className="mx-auto text-emerald-600" />
            <div className="font-heading font-bold">Password updated</div>
            <div className="text-sm text-muted-foreground">You can now sign in with your new password.</div>
            <Button className="w-full" onClick={() => nav("/login")} data-testid="reset-go-login">Go to Login</Button>
          </div>
        )}
      </div>
    </div>
  );
}
